import { Edit } from '@mui/icons-material'
import { Button, Card, CardContent, CircularProgress, Divider, Grid, Link, Typography } from '@mui/material'
import { ExplanationAccordion, GreenText } from 'components'
import * as controllers from 'controllers'
import { VehicleDto, VehicleExporterPhotosDto } from 'dtos'
import { enqueueSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import ExporterPhotoAddEditDialog from './ExporterPhotoAddEditDialog'

interface ExporterPhotoListCardProps {
  vehicle: VehicleDto
}

export default function ExporterPhotoListCard({ vehicle }: ExporterPhotoListCardProps) {
  const [vehicleExporterPhotos, setVehicleExporterPhotos] = useState<VehicleExporterPhotosDto>(new VehicleExporterPhotosDto())
  const [isSavingVehicleExporterPhoto, setIsSavingVehicleExporterPhoto] = useState<boolean>(false)
  const [ExporterPhotoAddEditDialogOpen, setExporterPhotoAddEditDialogOpen] = useState<boolean>(false)
  const [isGettingVehicleExporterPhotos, setIsGettingVehicleExporterPhotos] = useState<boolean>(true)

  useEffect(() => {
    console.log('vehicle', vehicle)
    if (vehicle.vehicleGuid) {
      setIsGettingVehicleExporterPhotos(true)
      controllers
        .getVehicleExporterPhotos(vehicle.vehicleGuid)
        .then(response => {
          const exporterPhotos = response.value
          setVehicleExporterPhotos(exporterPhotos)
        })
        .finally(() => {
          setIsGettingVehicleExporterPhotos(false)
        })
    }
  }, [vehicle])

  return (
    <>
      <ExporterPhotoAddEditDialog
        disabled={Boolean(vehicle.vehicleGuid && vehicle.isLocked)}
        initialValues={vehicleExporterPhotos}
        isLoading={isSavingVehicleExporterPhoto}
        onClose={() => {
          setExporterPhotoAddEditDialogOpen(false)
        }}
        onSave={values => {
          setIsSavingVehicleExporterPhoto(true)
          controllers
            .updateVehicleExporterPhotos(vehicle.vehicleGuid!, values)
            .then(response => {
              enqueueSnackbar('Exporter Photos Saved Successfully!', { variant: 'success' })
              setVehicleExporterPhotos(response.value)
              setExporterPhotoAddEditDialogOpen(false)
            })
            .finally(() => {
              setIsSavingVehicleExporterPhoto(false)
            })
        }}
        open={ExporterPhotoAddEditDialogOpen}
      />

      <Card variant='outlined'>
        <CardContent>
          <Grid container spacing={1} alignItems='flex-start'>
            <Grid item xs={12} container alignItems='center' justifyContent='space-between'>
              <Grid item xs={12} sm={6}>
                <Typography fontWeight='bold' variant='h5'>
                  Exporter Photos
                </Typography>
              </Grid>

              <Grid item xs={12} sm='auto'>
                <Button
                  onClick={() => {
                    setExporterPhotoAddEditDialogOpen(true)
                  }}
                  variant='text'
                  endIcon={<Edit />}
                >
                  EDIT
                </Button>
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            {isGettingVehicleExporterPhotos && (
              <Grid item container alignItems='center' justifyContent='center' sx={{ my: 1 }}>
                <Grid>
                  <CircularProgress color='primary' size={50} />
                </Grid>
              </Grid>
            )}

            {!isGettingVehicleExporterPhotos && (
              <>
                <Grid item xs={12}>
                  <Typography variant='body2'>Manufacturer Label Image (Exporter)</Typography>

                  {!vehicleExporterPhotos.manufacturerLabelImageExporterFileName && (
                    <Typography color='error' variant='body1'>
                      No file uploaded
                    </Typography>
                  )}

                  {vehicleExporterPhotos.manufacturerLabelImageExporterFileName && (
                    <Typography color='primary' variant='body1'>
                      <Link
                        onClick={() => {
                          controllers.getManufacturerLabelImageExporterFile(
                            vehicle.vehicleGuid!,
                            vehicleExporterPhotos.manufacturerLabelImageExporterFileName!
                          )
                        }}
                        sx={{ cursor: 'pointer' }}
                      >
                        {vehicleExporterPhotos.manufacturerLabelImageExporterFileName}
                      </Link>
                    </Typography>
                  )}
                </Grid>

                <Grid item xs={12}>
                  <Typography variant='body2'>TPMS Telltale Image (Exporter)</Typography>

                  {!vehicleExporterPhotos.tpmsTelltaleImageExporterFileName && (
                    <Typography color='error' variant='body1'>
                      No file uploaded
                    </Typography>
                  )}

                  {vehicleExporterPhotos.tpmsTelltaleImageExporterFileName && (
                    <Typography color='primary' variant='body1'>
                      <Link
                        onClick={() => {
                          controllers.getTPMSTelltaleImageExporterFile(
                            vehicle.vehicleGuid!,
                            vehicleExporterPhotos.tpmsTelltaleImageExporterFileName!
                          )
                        }}
                        sx={{ cursor: 'pointer' }}
                      >
                        {vehicleExporterPhotos.tpmsTelltaleImageExporterFileName}
                      </Link>
                    </Typography>
                  )}
                </Grid>

                <Grid item xs={12}>
                  <Typography variant='body2'>Odometer Image (Exporter)</Typography>

                  {!vehicleExporterPhotos.odometerImageExporterFileName && (
                    <Typography color='error' variant='body1'>
                      No file uploaded
                    </Typography>
                  )}

                  {vehicleExporterPhotos.odometerImageExporterFileName && (
                    <Typography color='primary' variant='body1'>
                      <Link
                        onClick={() => {
                          controllers.getOdometerImageExporterFile(
                            vehicle.vehicleGuid!,
                            vehicleExporterPhotos.odometerImageExporterFileName!
                          )
                        }}
                        sx={{ cursor: 'pointer' }}
                      >
                        {vehicleExporterPhotos.odometerImageExporterFileName}
                      </Link>
                    </Typography>
                  )}
                </Grid>
              </>
            )}

            <Grid item xs={12} sx={{ mt: 2 }}>
              <GreenText variant='outlined'>
                {`**Exporter Photos:**

The “Exporter” images allowed file types include .png, .jpg, and .jpeg. with a maximum file size of 20 MB.


---`}
              </GreenText>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </>
  )
}
